import React, { MutableRefObject, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Calendar, CalendarProps, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button, Dialog, DialogActions, DialogContent, Table } from '@material-ui/core';
import {
  DocumentTemplateType,
  INumberedDocumentView,
  IProjectCalendarEvent,
} from '../../api-client/autogenerated';
import CircularLoader from '../loader/CircularLoader';
import {
  documentTypeToReadable,
  documentTypeToUrl,
  getDocumentIdentifier,
  getUserFacingDocumentStatus,
  parseDate,
} from '../../scripts/utils';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getProjectId } from '../../features/project/selectors';
import dayjs from 'dayjs';
import { ICalCalendar } from 'ical-generator';
import DocumentIndexHeader from '../document-index/DocumentIndexHeader';
import getHeaders from '../document-index/DocumentLogHeaders';
import DocumentIndexRow from '../document-index/DocumentIndexRow';
import { ascendingComparator, getDocumentRows } from '../document-index/DocumentIndexUtils';
import getProjectsState from '../../features/projects/selectors';
import { useAppUtilsContext } from '../AppUtilsContext';
import { getDocumentById } from '../../models/api/documents';
import CalendarEvent, { EventType } from './CalendarEvent';

interface MonthDayCalendarProps extends Required<Pick<CalendarProps, 'views' | 'defaultView'>> {
  soonestDueDocuments: INumberedDocumentView[];
  projectEvents: IProjectCalendarEvent[];
  loading: boolean;
  calendarRef: MutableRefObject<any>;
  iCalendar: MutableRefObject<ICalCalendar>;
  calendarContainerClassName?: string;
}

const localizer = momentLocalizer(moment);

const useStyles = makeStyles({
  root: {
    fontFamily: 'Sans-Serif',
    font: 'Roboto',
    textTransform: 'none',
    width: '100%',
    marginTop: 20,
  },
  calendarStyling: {
    fontSize: '13px',
    padding: '0 0px',
    width: '100%',
    margin: 'auto',
    minHeight: '100vh',
    maxHeight: 'max-content',
    height: '100vh',
  },
});

const eventStyler = () => {
  return {
    style: {
      backgroundColor: '#EDECEC',
      borderLeft: '3px #4380ED solid',
    },
  };
};

const formats = {
  eventTimeRangeFormat: () => {
    return '';
  },
};

const reactBigCalenderClasses = ` .rbc-overlay { max-height: 400px; overflow: scroll }
                                  .rbc-overlay > div { margin-bottom: 4px }
                                  .rbc-time-header, .rbc-time-header-content, .rbc-allday-cell, .rbc-row-bg, .rbc-row-content { height: 100%; overflow-y: scroll }`;

export default function MonthDayCalendar(props: MonthDayCalendarProps) {
  const {
    soonestDueDocuments,
    projectEvents,
    loading,
    calendarRef,
    iCalendar,
    views,
    defaultView,
    calendarContainerClassName,
  } = props;
  const classes = useStyles();

  const history = useHistory();
  const projects = useSelector(getProjectsState);
  const projectId = useSelector(getProjectId);

  const {
    documentTemplates: { documentTemplateIdToName },
  } = useAppUtilsContext();

  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<EventType | null>(null);
  const [data, setData] = useState<EventType[]>([]);

  const handleClickOpen = (event: EventType) => {
    setOpen(true);
    getDocumentById(event.parentId || event.id, true).then(async (document) => {
      setDialogContent({
        ...event,
        documentIndexRow: (await getDocumentRows([document], (event.parentType || event.type)!))[0],
      });
    });
  };
  const handleClose = () => {
    setOpen(false);
    setDialogContent(null);
  };

  const getRedirectLink = (event: EventType) => {
    if (!event.type) return;

    if (event.type === DocumentTemplateType.PunchList) {
      return `/main/projects/${event.projectId}/documents/punch-list/${event.id}/edit`;
    }

    if (event.type !== DocumentTemplateType.AdditionalReview) {
      return `/main/projects/${event.projectId}/documents/${
        documentTypeToUrl[event.type as DocumentTemplateType]
      }/${event.id}`;
    }

    if (event.parentId && event.parentType) {
      return `/main/projects/${event.projectId}/documents/${
        documentTypeToUrl[event.parentType as DocumentTemplateType]
      }/${event.parentId}`;
    }
  };

  const handleSelectDocument = async (event: EventType) => {
    const link = getRedirectLink(event);
    if (link) {
      history.push(link);
    }
  };

  const getCalendarEvents = async () => {
    const soonestDueDocumentsWithExistingProject = soonestDueDocuments
      .map((doc) => ({
        ...doc,
        project: projects.find((p) => p.id === doc.projectId),
      }))
      .filter(
        (doc) =>
          !!doc.project && doc.project.isHidden === false && doc.project.isArchived === false,
      ) as INumberedDocumentView[];
    const documentCalendarEvents = await Promise.all(
      soonestDueDocumentsWithExistingProject.map(async (document) => {
        if (!document.dueDate) return null;
        const dueDate = parseDate(document.dueDate).startOf('day').toDate();

        if (!document.project) return null;

        const documentType = documentTemplateIdToName[document.documentTemplateId];
        const parentDocumentType = document.additionalReviewForDocument
          ? documentTemplateIdToName[document.additionalReviewForDocument.documentTemplateId]
          : undefined;

        const newCalendarEvent: EventType = {
          id: document.id,
          projectId: document.projectId,
          title: getUserFacingDocumentStatus(document, documentType),
          start: dueDate,
          end: dueDate,
          allDay: true,
          source: document.additionalReviewForDocument
            ? getDocumentIdentifier(document.additionalReviewForDocument, parentDocumentType!, {
                onlyNumber: true,
              })
            : getDocumentIdentifier(document, documentType, {
                onlyNumber: true,
              }),
          company: projectId ? '' : document.project.name,
          type: documentType,
          typeReadable: document.additionalReviewForDocument
            ? `Consultant Review for ${documentTypeToReadable[parentDocumentType!]}`
            : documentTypeToReadable[documentType],
          parentId: document.additionalReviewForDocumentId || undefined,
          parentType: parentDocumentType,
          documentIndexRow: (document.additionalReviewForDocument
            ? await getDocumentRows([document.additionalReviewForDocument], parentDocumentType!)
            : await getDocumentRows([document], documentType))[0],
        };

        const link = getRedirectLink(newCalendarEvent);

        iCalendar.current.createEvent({
          id: document.id,
          start: dueDate,
          end: dueDate,
          allDay: true,
          summary: `${newCalendarEvent.type} ${newCalendarEvent.source}`,
          description: `Project: ${document.project.name}`,
          url: link ? `https://centerline.co${link}` : undefined,
        });

        return newCalendarEvent;
      }),
    );

    const projectCalendarEvents = projectId
      ? projectEvents.map(({ id, name, startTimestamp, endTimestamp }) => {
          const start = parseDate(startTimestamp).toDate();
          const end = parseDate(endTimestamp).toDate();
          const newEvent: EventType = {
            id,
            projectId,
            title: name,
            start,
            end,
          };

          iCalendar.current.createEvent(newEvent);

          return newEvent;
        })
      : [];

    return ([...documentCalendarEvents, ...projectCalendarEvents].filter(
      (event) => !!event,
    ) as EventType[]).sort(
      (a, b) => ascendingComparator(a, b, 'typeReadable') || ascendingComparator(a, b, 'source'),
    );
  };

  useEffect(() => {
    iCalendar.current.clear();

    getCalendarEvents().then((events) => {
      setData(events);
    });
  }, [soonestDueDocuments, projectEvents, projectId]);

  return (
    <div className={classes.root}>
      <style>{reactBigCalenderClasses}</style>
      {loading && (
        <div style={{ position: 'relative', top: '50%', padding: 200 }}>
          <CircularLoader />
        </div>
      )}

      {!loading && (
        <div>
          <div className={calendarContainerClassName}>
            <Calendar
              ref={calendarRef}
              formats={formats}
              events={data}
              step={30}
              showMultiDayTimes
              defaultDate={dayjs().toDate()}
              defaultView={defaultView}
              components={{
                event: CalendarEvent,
              }}
              popup
              localizer={localizer}
              eventPropGetter={eventStyler}
              onSelectEvent={(event) => {
                handleClickOpen(event);
              }}
              views={views}
            />
          </div>
          <Dialog onClose={handleClose} open={open} maxWidth="lg">
            <DialogContent>
              {dialogContent?.documentIndexRow ? (
                <Table size="small">
                  <DocumentIndexHeader
                    columnTitles={
                      getHeaders((dialogContent?.parentType || dialogContent?.type)!) || []
                    }
                    hideCheckboxes
                    order="desc"
                    orderBy="number"
                    numSelected={0}
                    filteredRows={[]}
                    onSelectAllClick={() => {}}
                    onRequestSort={() => {}}
                    notHiddenRowCount={0}
                    isCalendar
                  />
                  <DocumentIndexRow
                    isAssociatedDocumentsList
                    trueDocType={dialogContent?.parentType}
                    row={dialogContent?.documentIndexRow || []}
                    index={0}
                    onSelect={() => handleSelectDocument(dialogContent!)}
                    isEditingCost={false}
                    updateRowCost={() => {}}
                  />
                </Table>
              ) : (
                <CircularLoader />
              )}
            </DialogContent>
            <DialogActions>
              <Button
                autoFocus
                onClick={() => handleSelectDocument(dialogContent!)}
                color="primary"
              >
                View
              </Button>
              <Button autoFocus onClick={handleClose} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  );
}
